import { useEffect, useState, type CSSProperties } from 'react';
import { EyeOff, Eye } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';

interface TimelineEntry {
  date: string;       // YYYY-MM-DD
  presence: string;   // 'present' | 'absent' | 'expected' | 'added' | …
}

interface ThemeEntry {
  color: string;
  name: string;
  count: number;
}

interface ParticipantOverview {
  key: string;
  v2_participant_id: number | null;
  display_name: string;
  /** The logged-in animator is this participant's current référent-e de mesure. */
  is_reference?: boolean;
  archived?: boolean;
  pending_confirmation?: boolean;
  hidden?: boolean;
  animators?: string[];
  last_session_date: string | null;
  next_session_date: string | null;
  attendance_count: number;
  sessions_count: number;
  timeline?: TimelineEntry[];
  themes?: ThemeEntry[];
}

interface OverviewResponse {
  participants: ParticipantOverview[];
  v2_available: boolean;
  scope: 'mine' | 'all';
}

interface Props {
  onOpenParticipant: (v2Id: number | null, name: string) => void;
}

const SCOPE_KEY = 'evolution.participants.scope';

function localTodayIso(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

function daysBetween(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.split('-').map(Number);
  const [ty, tm, td] = toIso.split('-').map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
}

type MarkKind = 'present' | 'missed' | 'upcoming';

interface Mark {
  date: string;
  kind: MarkKind;
  pos: number; // 0..100 (%)
}

/**
 * Build the little présence timeline: one mark per session, placed
 * proportionally by date so the *rhythm* of the meetings is visible.
 * Marks closer than minGap% are nudged apart (then re-fit to 100).
 */
function buildMarks(timeline: TimelineEntry[], nextDate: string | null, today: string): Mark[] {
  const entries = timeline.filter(e => e.presence !== 'not_recorded').sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  if (nextDate && !entries.some((e) => e.date === nextDate)) {
    entries.push({ date: nextDate, presence: 'expected' });
    entries.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }
  if (entries.length === 0) return [];

  const first = entries[0].date;
  const last = entries[entries.length - 1].date;
  const span = Math.max(daysBetween(first, last), 1);
  const single = entries.length === 1;

  const marks: Mark[] = entries.map((e) => ({
    date: e.date,
    kind: e.date >= today ? 'upcoming' : e.presence === 'present' ? 'present' : 'missed',
    pos: single ? 50 : (daysBetween(first, e.date) / span) * 100,
  }));

  const minGap = Math.min(4, 100 / Math.max(marks.length - 1, 1));
  for (let i = 1; i < marks.length; i++) {
    if (marks[i].pos < marks[i - 1].pos + minGap) marks[i].pos = marks[i - 1].pos + minGap;
  }
  const overflow = marks[marks.length - 1].pos;
  if (overflow > 100) marks.forEach((m) => (m.pos = (m.pos / overflow) * 100));

  return marks;
}

export function MyParticipantsPage({ onOpenParticipant }: Props) {
  const { t, lang } = useT();
  const [scope, setScope] = useState<'mine' | 'all'>(() => (localStorage.getItem(SCOPE_KEY) === 'all' ? 'all' : 'mine'));
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState('');
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { localStorage.setItem(SCOPE_KEY, scope); }, [scope]);

  const load = () => {
    setData(null); setError(null);
    api.get<OverviewResponse>(`/api/my-participants?scope=${scope}${showArchived ? '&archived=1' : ''}`)
      .then(setData)
      .catch((e) => setError(String(e)));
  };
  useEffect(load, [scope, showArchived]);   // eslint-disable-line react-hooks/exhaustive-deps

  const hide = async (key: string, hidden: boolean) => {
    await api.post(`/api/my-participants/${hidden ? 'unhide' : 'hide'}`, { key });
    load();
  };

  const reactivate = async (key: string) => {
    const reason = window.prompt('Motif de réactivation (en attente de confirmation dans Éveil)');
    if (!reason?.trim()) return;
    try { await api.post('/api/my-participants/reactivate', { key, reason }); load(); }
    catch (e) { setError(String(e)); }
  };

  const locale = lang === 'fr' ? 'fr-CH' : 'en-CH';

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  /** Two-line mark label: day number over abbreviated month ("10" / "avr."). */
  const dayOf = (iso: string) => String(Number(iso.split('-')[2]));
  const monthOf = (iso: string) => {
    const [y, m] = iso.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'short' });
  };

  /** "depuis juin 2026" */
  const formatMonthYear = (iso: string) => {
    const [y, m] = iso.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  const today = localTodayIso();

  const countdown = (nextIso: string): string => {
    const n = daysBetween(today, nextIso);
    if (n <= 0) return t('participants.today');
    if (n === 1) return t('participants.tomorrow');
    return `${t('participants.inPrefix')} ${n} ${n === 1 ? t('participants.dayUnit') : t('participants.daysUnit')}`;
  };

  const markTitle = (m: Mark): string => {
    const label = m.kind === 'present'
      ? t('session.presence.present')
      : m.kind === 'upcoming'
        ? t('participants.upcoming')
        : t('session.presence.absent');
    return `${formatDate(m.date)} — ${label}`;
  };

  const q = filter.trim().toLowerCase();
  const shown = (data?.participants ?? []).filter((p) => !q || p.display_name.toLowerCase().includes(q));

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 style={{ margin: 0 }}>{scope === 'all' ? t('participants.titleAll') : t('participants.title')}</h1>
          <div className="scope-toggle" role="radiogroup" aria-label={t('participants.scope')}>
            <button type="button" role="radio" aria-checked={scope === 'mine'} className={`scope-toggle-btn ${scope === 'mine' ? 'scope-toggle-btn--active' : ''}`} onClick={() => setScope('mine')}>
              {t('participants.scope.mine')}
            </button>
            <button type="button" role="radio" aria-checked={scope === 'all'} className={`scope-toggle-btn ${scope === 'all' ? 'scope-toggle-btn--active' : ''}`} onClick={() => setScope('all')}>
              {t('participants.scope.all')}
            </button>
          </div>
          <label className="psearch-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            {t('participants.showArchived')}
          </label>
        </div>
        <div className="page-header-right">
          <input className="field-input usage-filter" placeholder={t('common.search')} value={filter} onChange={(e) => setFilter(e.target.value)} />
          {data && data.participants.length > 0 && (
            <span className="page-header-meta">
              {shown.length} — {shown.reduce((n, p) => n + p.sessions_count, 0)} {t('participants.sessions')}
            </span>
          )}
        </div>
      </div>

      {error && <div className="empty-state" style={{ color: 'var(--err)' }}>{error}</div>}
      {!data && !error && <div className="empty-state">{t('common.loading')}</div>}
      {data && data.participants.length === 0 && (
        <div className="empty-state">{t('participants.empty')}</div>
      )}

      {data && !data.v2_available && data.participants.length > 0 && (
        <div className="pt-v2-warning">{t('participant.v2Unavailable')}</div>
      )}

      {data && shown.length > 0 && (
        <div className="pt-grid">
          {shown.map((p, ci) => {
            const marks = buildMarks(p.timeline ?? [], p.next_session_date, today);
            const pastMarks = marks.filter((m) => m.kind !== 'upcoming');
            const firstPast = pastMarks.length > 0 ? pastMarks[0].date : null;
            const lastPast = pastMarks.length > 0 ? pastMarks[pastMarks.length - 1].date : null;
            const daysAround = firstPast && lastPast ? daysBetween(firstPast, lastPast) + 1 : 0;

            // Cadence: the average interval between attended sessions, in weeks.
            const presents = pastMarks.filter((m) => m.kind === 'present');
            const cadenceWeeks = presents.length >= 2
              ? daysBetween(presents[0].date, presents[presents.length - 1].date) / (presents.length - 1) / 7
              : null;
            const cadenceLabel = cadenceWeeks != null
              ? (() => {
                  const rounded = Math.round(cadenceWeeks * 10) / 10;
                  const txt = Number.isInteger(rounded)
                    ? String(rounded)
                    : rounded.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                  return `${txt} ${rounded >= 2 ? t('participants.weeks') : t('participants.week')}`;
                })()
              : null;
            const themes = (p.themes ?? []).slice(0, 4);
            const seancesWord = p.sessions_count === 1 ? t('participants.session') : t('participants.sessions');

            let lastLabelPos = -100;
            const labelled = marks.map((m) => {
              const show = m.pos - lastLabelPos >= 8;
              if (show) lastLabelPos = m.pos;
              return show;
            });

            return (
              <div
                key={p.key}
                className={`card card-clickable pt-card ptv-card ${p.archived ? 'ptv-card--archived' : ''}`}
                style={{ '--ci': ci } as CSSProperties}
                onClick={() => onOpenParticipant(p.v2_participant_id, p.display_name)}
              >
                <div className="ptv-head">
                  <div className="ptv-head-text">
                    <div className="pt-card-name">
                      {p.display_name}
                      {p.is_reference && (
                        <span
                          className="badge badge-accent"
                          style={{ marginLeft: 8, verticalAlign: 'middle' }}
                          title={t('participants.referenceTitle')}
                        >
                          {t('participants.reference')}
                        </span>
                      )}
                      {p.pending_confirmation && <span className="badge badge-accent">Inscription à confirmer</span>}
                      {p.archived && (
                        <span className="badge badge-muted" style={{ marginLeft: 8, verticalAlign: 'middle' }} title={t('participants.archivedHelp')}>
                          {p.hidden ? t('participants.hidden') : t('participants.archived')}
                        </span>
                      )}
                    </div>
                    <div className="ptv-tenure">
                      {firstPast
                        ? <>{t('participants.since')} {formatMonthYear(firstPast)} · {p.sessions_count} {seancesWord}</>
                        : p.next_session_date
                          ? <>{t('participants.firstMeeting')} {t('participants.upcoming')}</>
                          : <>{t('participants.noSessionsYet')}</>}
                      {scope === 'all' && p.animators && p.animators.length > 0 && (
                        <> · {t('participants.animators')} {p.animators.join(', ')}</>
                      )}
                    </div>
                    {firstPast && (daysAround > 1 || cadenceLabel) && (
                      <div className="ptv-tenure ptv-tenure--stats">
                        {daysAround > 1 && <>{daysAround} {t('participants.daysUnit')}</>}
                        {cadenceLabel && (
                          <>
                            {daysAround > 1 && ' · '}
                            {t('participants.cadence')} : <strong>{cadenceLabel}</strong>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="ptv-head-right">
                    {themes.length > 0 && (
                      <span
                        className="ptv-fan"
                        title={`${t('participants.themesTitle')} : ${themes.map((th) => th.name).join(', ')}`}
                        aria-label={`${t('participants.themesTitle')} : ${themes.map((th) => th.name).join(', ')}`}
                        role="img"
                      >
                        {themes.map((th, i) => <i key={i} style={{ background: th.color }} />)}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm ptv-hide"
                      title={p.archived ? 'Réactiver' : t('participants.hide')}
                      aria-label={p.archived ? 'Réactiver' : t('participants.hide')}
                      onClick={(e) => { e.stopPropagation(); if (p.archived && p.v2_participant_id) reactivate(p.key); else hide(p.key, !!p.hidden); }}
                    >
                      {p.archived ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                  </div>
                </div>

                {marks.length > 0 && (
                  <div
                    className="ptv-timeline"
                    role="img"
                    aria-label={`${p.attendance_count} ${t('participants.presencesOf')} ${p.sessions_count} ${seancesWord}`}
                  >
                    <div className="ptv-tl-kicker">{t('participants.attendance')}</div>
                    <div className="ptv-track">
                      {marks.map((m, i) => (
                        <span
                          key={`${m.date}-${i}`}
                          className={`ptv-mark ptv-mark--${m.kind}`}
                          style={{ left: `${m.pos}%`, '--i': i } as CSSProperties}
                          title={markTitle(m)}
                        />
                      ))}
                    </div>
                    <div className="ptv-dates">
                      {marks.map((m, i) => labelled[i] && (
                        <span
                          key={`${m.date}-${i}`}
                          className={`ptv-date${m.kind === 'upcoming' ? ' ptv-date--next' : ''}${m.pos < 4 ? ' ptv-date--first' : ''}${m.pos > 96 ? ' ptv-date--last' : ''}`}
                          style={{ left: `${m.pos}%`, '--i': i } as CSSProperties}
                          title={markTitle(m)}
                        >
                          <span className="ptv-date-day">{dayOf(m.date)}</span>
                          <span className="ptv-date-month">{monthOf(m.date)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ptv-foot">
                  {p.next_session_date ? (
                    <span className="ptv-next" title={formatDate(p.next_session_date)}>
                      {t('participants.nextSession')} · <strong>{countdown(p.next_session_date)}</strong>
                    </span>
                  ) : (
                    <span className="ptv-last">
                      {t('participants.lastSession')} · {p.last_session_date ? formatDate(p.last_session_date) : '—'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
